import { FabricObject, ElementName } from '@meitu/whee-infinite-canvas';
import { useLanguageStore } from '$/store/languageStore';

// 不支持的FabricObject复制类型
const UNSUPPORTED_FABRIC_OBJECT_TYPES = [
    ElementName.VIDEO,  // 视频
    ElementName.DYNAMIC_PICTURE,  // 动图
];

/**
 * 将 Fabric.js 画布对象复制为媒体文件并写入剪贴板
 * 
 * 1.不支持批量复制，虽然api支持，但是浏览器目前不支持
 * 
 * 2.不支持视频、gif复制
 * 
 * 3.只支持单个图片的复制，复制图片格式为png
 * 
 * 4.如果复制了不支持的格式或图片，会以文字提示的方式复制到剪贴板
 * 
 * @link 类型 https://developer.mozilla.org/en-US/docs/Web/API/ClipboardItem/supports_static
 * @link clipboard api https://developer.mozilla.org/zh-CN/docs/Web/API/Clipboard
 */
export async function copyFabricObjectToClipboard(objs: FabricObject[]) {
    // 判断浏览器是否支持剪贴板写入
    if (!canWriteToClipboard() || objs.length === 0) {
      return;
    }

    const { canCopy, copyText } = checkExtensionAndCount(objs);

    // 如果不能复制，则复制提示文案
    if (!canCopy) {
      await copyTextToClipboard(copyText);
      return;
    }

    // 只能单个复制
    const obj = objs[0];
    const blobPromise = obj.toBlob({ format: 'png' });
    // 创建ClipboardItem时，值为Promise类型的blob，兼容 safari 浏览器，safari 复制时，必须在事件方法中同步执行，否则会报错
    const item = new ClipboardItem({ 
      'image/png': blobPromise.then(blob => {
        if (!blob) {
          throw new Error('无法获取 blob');
        }
        return blob;
      })
    });
    try {
        // 虽然参数是数组，但是浏览器只支持单个复制
      await navigator.clipboard.write([item]);
    } catch (error) {
      console.error('[copyFabricObjectToClipboard]复制失败', error);
    }
  }

  function canWriteToClipboard(): boolean {
    return !!(navigator.clipboard &&
      window.ClipboardItem &&
      ClipboardItem.supports)
  }

  /**
   * 将文本写入剪贴板
   */
  export async function copyTextToClipboard(text: string) {
    // 判断浏览器是否支持剪贴板写入，如果不支持则降级处理，使用 textarea 元素实现
    if (!canWriteToClipboard()) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return;
    } else {
      try {
        await navigator.clipboard.writeText(text);
      } catch (error) {
        console.error('[copyTextToClipboard]复制失败', error);
      }
    }
  }

  /**
   * 校验扩展名和数量，返回提示文案
   */
  function checkExtensionAndCount(objs: FabricObject[]): {
    canCopy: boolean;
    copyText: string;
  } {
    const hasUnsupportedExtension = objs.some(obj => UNSUPPORTED_FABRIC_OBJECT_TYPES.includes(obj._name_ as ElementName));
    const isBatchCopy = objs.length > 1;
    let copyText = '';
    if (hasUnsupportedExtension || isBatchCopy) {
      copyText = useLanguageStore.getState().languageStore.smartcatTranslationText((v) => v.roboneo_web_unsupported_format_count);
    }
    return { canCopy: !copyText, copyText };
  }
  